/**
 * Deal Slice
 * Redux Toolkit slice for deal state management
 */

import { createSlice, createAsyncThunk, createSelector, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../index';
import type {
  Deal,
  DealStage,
  DealPriority,
  CreateDealRequest,
  UpdateDealRequest,
  UpdateDealStageRequest,
} from '@creatoros/shared';
import { DEAL_STAGE_ORDER } from '@creatoros/shared';
import { dealService } from '../../services/deal.service';
import { asArray } from '../../services/api.unwrap';
import { selectBrands } from './brandSlice';
import { DateRange, getDateRangeStart } from '../../types/dashboard';
import { ApiErrorState } from '../../types/error.types';
import { logger } from '../../utils/logger';

/**
 * Deal State Interface
 */
export interface DealState {
  deals: Deal[];
  /** Accurate total count from DB (not bounded by page size). Null until first fetch. */
  totalCount: number | null;
  /** Accurate count of active (non-completed) deals from DB. Null until first fetch. */
  totalActive: number | null;
  selectedDeal: Deal | null;
  isLoading: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  error: ApiErrorState | null;
  filters: {
    search?: string;
    brandIds?: string[];
    brandId?: string;
    stage?: DealStage;
    priority?: DealPriority;
  };
}

/**
 * Initial State
 */
const initialState: DealState = {
  deals: [],
  totalCount: null,
  totalActive: null,
  selectedDeal: null,
  isLoading: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  error: null,
  filters: {},
};

/**
 * Async Thunks
 */

/**
 * Fetch all deals
 */
export const fetchDeals = createAsyncThunk<
  { deals: Deal[]; total: number | null; totalActive: number | null },
  void,
  { rejectValue: ApiErrorState }
>(
  'deals/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      logger.debug('Fetching deals', { event: 'deals.fetch.started' });
      const response = await dealService.list();
      const deals = asArray<Deal>(response.data);
      logger.debug('Fetched deals', {
        event: 'deals.fetch.succeeded',
        count: deals.length,
      });
      return {
        deals,
        total: response.total ?? null,
        totalActive: response.totalActive ?? null,
      };
    } catch (err: any) {
      logger.error('Failed to fetch deals', err, {
        event: 'deals.fetch.failed',
        code: err.code || err.response?.data?.errorType,
      });
      return rejectWithValue({
        message: err.message || err.response?.data?.error || 'Failed to fetch deals',
        code: err.code || err.response?.data?.errorType,
        details: err.details || err.response?.data?.details,
        timestamp: new Date().toISOString(),
      });
    }
  },
  {
    // Prevent duplicate in-flight fetches.  When multiple components dispatch
    // fetchDeals() simultaneously (e.g. DashboardPage + DashboardLayout), the
    // second dispatch is silently skipped while the first is still pending.
    condition: (_, { getState }) => {
      const { deals } = getState() as { deals: { isLoading: boolean } };
      return !deals.isLoading;
    },
  }
);

/**
 * Fetch deals by brand ID
 */
export const fetchDealsByBrand = createAsyncThunk<Deal[], string, { rejectValue: ApiErrorState }>(
  'deals/fetchByBrand',
  async (brandId, { rejectWithValue }) => {
    try {
      logger.debug('Fetching deals by brand', {
        event: 'deals.fetch_by_brand.started',
        brandId,
      });
      const response = await dealService.getByBrand(brandId);
      const deals = asArray<Deal>(response.data);
      logger.debug('Fetched deals by brand', {
        event: 'deals.fetch_by_brand.succeeded',
        brandId,
        count: deals.length,
      });
      return deals;
    } catch (err: any) {
      logger.error('Failed to fetch deals by brand', err, {
        event: 'deals.fetch_by_brand.failed',
        brandId,
        code: err.code || err.response?.data?.errorType,
      });
      return rejectWithValue({
        message: err.message || err.response?.data?.error || 'Failed to fetch deals',
        code: err.code || err.response?.data?.errorType,
        details: err.details || err.response?.data?.details,
        timestamp: new Date().toISOString(),
      });
    }
  }
);

/**
 * Fetch a single deal by ID
 */
export const fetchDealById = createAsyncThunk<Deal, string, { rejectValue: ApiErrorState }>(
  'deals/fetchById',
  async (id, { rejectWithValue }) => {
    try {
      logger.debug('Fetching deal', { event: 'deals.fetch_by_id.started', dealId: id });
      const deal = await dealService.getById(id);
      logger.debug('Fetched deal', { event: 'deals.fetch_by_id.succeeded', dealId: id });
      return deal;
    } catch (err: any) {
      logger.error('Failed to fetch deal', err, {
        event: 'deals.fetch_by_id.failed',
        dealId: id,
        code: err.code || err.response?.data?.errorType,
      });
      return rejectWithValue({
        message: err.message || err.response?.data?.error || 'Failed to fetch deal',
        code: err.code || err.response?.data?.errorType,
        details: err.details || err.response?.data?.details,
        timestamp: new Date().toISOString(),
      });
    }
  }
);

/**
 * Create a new deal
 */
export const createDeal = createAsyncThunk<Deal, CreateDealRequest, { rejectValue: ApiErrorState }>(
  'deals/create',
  async (data, { rejectWithValue }) => {
    try {
      const deal = await dealService.create(data);
      logger.info('Deal created', { event: 'deals.create.succeeded', dealId: deal.id });
      return deal;
    } catch (err: any) {
      logger.error('Failed to create deal', err, {
        event: 'deals.create.failed',
        code: err.code || err.response?.data?.errorType,
      });
      return rejectWithValue({
        message: err.message || err.response?.data?.error || 'Failed to create deal',
        code: err.code || err.response?.data?.errorType,
        details: err.details || err.response?.data?.details,
        timestamp: new Date().toISOString(),
      });
    }
  }
);

/**
 * Update an existing deal
 */
export const updateDeal = createAsyncThunk<
  Deal,
  { id: string; data: UpdateDealRequest },
  { rejectValue: ApiErrorState }
>('deals/update', async ({ id, data }, { rejectWithValue }) => {
  try {
    const deal = await dealService.update(id, data);
    logger.info('Deal updated', { event: 'deals.update.succeeded', dealId: id });
    return deal;
  } catch (err: any) {
    logger.error('Failed to update deal', err, {
      event: 'deals.update.failed',
      dealId: id,
      code: err.code || err.response?.data?.errorType,
    });
    return rejectWithValue({
      message: err.message || err.response?.data?.error || 'Failed to update deal',
      code: err.code || err.response?.data?.errorType,
      details: err.details || err.response?.data?.details,
      timestamp: new Date().toISOString(),
    });
  }
});

/**
 * Update deal stage
 */
export const updateDealStage = createAsyncThunk<
  Deal,
  { id: string; data: UpdateDealStageRequest },
  { rejectValue: ApiErrorState }
>('deals/updateStage', async ({ id, data }, { rejectWithValue }) => {
  try {
    const deal = await dealService.updateStage(id, data);
    logger.info('Deal stage updated', {
      event: 'deals.update_stage.succeeded',
      dealId: id,
    });
    return deal;
  } catch (err: any) {
    logger.error('Failed to update deal stage', err, {
      event: 'deals.update_stage.failed',
      dealId: id,
      code: err.code || err.response?.data?.errorType,
    });
    return rejectWithValue({
      message: err.message || err.response?.data?.error || 'Failed to update deal stage',
      code: err.code || err.response?.data?.errorType,
      details: err.details || err.response?.data?.details,
      timestamp: new Date().toISOString(),
    });
  }
});

/**
 * Delete a deal
 */
export const deleteDeal = createAsyncThunk<string, string, { rejectValue: ApiErrorState }>(
  'deals/delete',
  async (id, { rejectWithValue }) => {
    try {
      await dealService.delete(id);
      logger.info('Deal deleted', { event: 'deals.delete.succeeded', dealId: id });
      return id;
    } catch (err: any) {
      logger.error('Failed to delete deal', err, {
        event: 'deals.delete.failed',
        dealId: id,
        code: err.code || err.response?.data?.errorType,
      });
      return rejectWithValue({
        message: err.message || err.response?.data?.error || 'Failed to delete deal',
        code: err.code || err.response?.data?.errorType,
        details: err.details || err.response?.data?.details,
        timestamp: new Date().toISOString(),
      });
    }
  }
);

/**
 * Deal Slice
 */
const dealSlice = createSlice({
  name: 'deals',
  initialState,
  reducers: {
    /**
     * Set selected deal
     */
    setSelectedDeal: (state, action: PayloadAction<Deal | null>) => {
      state.selectedDeal = action.payload;
    },

    /**
     * Clear selected deal
     */
    clearSelectedDeal: (state) => {
      state.selectedDeal = null;
    },

    /**
     * Set filters
     */
    setDealFilters: (
      state,
      action: PayloadAction<{
        search?: string;
        brandIds?: string[];
        brandId?: string;
        stage?: DealStage;
        priority?: DealPriority;
      }>
    ) => {
      state.filters = { ...state.filters, ...action.payload };
    },

    /**
     * Clear filters
     */
    clearDealFilters: (state) => {
      state.filters = {};
    },

    /**
     * Clear error
     */
    clearError: (state) => {
      state.error = null;
    },

    /**
     * Reset state
     */
    resetDealState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Fetch all deals
      .addCase(fetchDeals.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDeals.fulfilled, (state, action) => {
        state.isLoading = false;
        state.deals = asArray(action.payload.deals);
        state.totalCount = action.payload.total;
        state.totalActive = action.payload.totalActive;
      })
      .addCase(fetchDeals.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? { message: 'Failed to fetch deals' };
      })

      // Fetch deals by brand
      .addCase(fetchDealsByBrand.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDealsByBrand.fulfilled, (state, action) => {
        state.isLoading = false;
        state.deals = asArray(action.payload);
      })
      .addCase(fetchDealsByBrand.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? { message: 'Failed to fetch deals' };
      })

      // Fetch deal by ID
      .addCase(fetchDealById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDealById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedDeal = action.payload;
        // Update in list if exists
        const index = state.deals.findIndex((d) => d.id === action.payload.id);
        if (index !== -1) {
          state.deals[index] = action.payload;
        }
      })
      .addCase(fetchDealById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? { message: 'Failed to fetch deal' };
      })

      // Create deal
      .addCase(createDeal.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createDeal.fulfilled, (state, action) => {
        state.isCreating = false;
        state.deals.push(action.payload);
        if (state.totalCount !== null) {
          state.totalCount += 1;
        }
        // New deals start in a non-completed stage, so they are active
        if (state.totalActive !== null && action.payload.stage !== 'completed') {
          state.totalActive += 1;
        }
      })
      .addCase(createDeal.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload ?? { message: 'Failed to create deal' };
      })

      // Update deal
      .addCase(updateDeal.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateDeal.fulfilled, (state, action) => {
        state.isUpdating = false;
        const index = state.deals.findIndex((d) => d.id === action.payload.id);
        if (index !== -1) {
          state.deals[index] = action.payload;
        }
        if (state.selectedDeal?.id === action.payload.id) {
          state.selectedDeal = action.payload;
        }
      })
      .addCase(updateDeal.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload ?? { message: 'Failed to update deal' };
      })

      // Update deal stage
      .addCase(updateDealStage.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateDealStage.fulfilled, (state, action) => {
        state.isUpdating = false;
        const index = state.deals.findIndex((d) => d.id === action.payload.id);
        if (index !== -1) {
          const oldStage = state.deals[index]!.stage;
          const newStage = action.payload.stage;
          state.deals[index] = action.payload;
          // Adjust totalActive for stage transitions into/out of 'completed'
          if (state.totalActive !== null) {
            if (oldStage !== 'completed' && newStage === 'completed') {
              state.totalActive -= 1;
            } else if (oldStage === 'completed' && newStage !== 'completed') {
              state.totalActive += 1;
            }
          }
        }
        if (state.selectedDeal?.id === action.payload.id) {
          state.selectedDeal = action.payload;
        }
      })
      .addCase(updateDealStage.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload ?? { message: 'Failed to update deal stage' };
      })

      // Delete deal
      .addCase(deleteDeal.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deleteDeal.fulfilled, (state, action) => {
        state.isDeleting = false;
        const deleted = state.deals.find((d) => d.id === action.payload);
        state.deals = state.deals.filter((d) => d.id !== action.payload);
        if (state.selectedDeal?.id === action.payload) {
          state.selectedDeal = null;
        }
        if (state.totalCount !== null) {
          state.totalCount -= 1;
        }
        // Adjust totalActive if the deleted deal was not completed
        if (state.totalActive !== null && deleted && deleted.stage !== 'completed') {
          state.totalActive -= 1;
        }
      })
      .addCase(deleteDeal.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload ?? { message: 'Failed to delete deal' };
      });
  },
});

/**
 * Selectors
 */

// Base selectors
export const selectDeals = (state: RootState) => asArray<Deal>(state.deals.deals);
export const selectSelectedDeal = (state: RootState) => state.deals.selectedDeal;
export const selectDealError = (state: RootState) => state.deals.error;
export const selectDealIsLoading = (state: RootState) => state.deals.isLoading;
export const selectDealIsCreating = (state: RootState) => state.deals.isCreating;
export const selectDealIsUpdating = (state: RootState) => state.deals.isUpdating;
export const selectDealIsDeleting = (state: RootState) => state.deals.isDeleting;
export const selectDealFilters = (state: RootState) => state.deals.filters;

// Derived selectors
export const selectDealById = (state: RootState, id: string) =>
  selectDeals(state).find((deal) => deal.id === id);

export const selectDealsByBrand = (state: RootState, brandId: string) =>
  selectDeals(state).filter((deal) => deal.brandId === brandId);

export const selectDealsByStage = (state: RootState, stage: DealStage) =>
  selectDeals(state).filter((deal) => deal.stage === stage);

export const selectDealsByPriority = (state: RootState, priority: DealPriority) =>
  selectDeals(state).filter((deal) => deal.priority === priority);

// Selector for deals in progress (not completed)
export const selectActiveDeals = createSelector([selectDeals], (deals) =>
  deals.filter((deal) => deal.stage !== 'completed')
);

export const selectFilteredDeals = (state: RootState) => {
  let filtered = selectDeals(state);
  const { brandId, stage, priority } = state.deals.filters;

  if (brandId) {
    filtered = filtered.filter((deal) => deal.brandId === brandId);
  }
  if (stage) {
    filtered = filtered.filter((deal) => deal.stage === stage);
  }
  if (priority) {
    filtered = filtered.filter((deal) => deal.priority === priority);
  }

  return filtered;
};

/**
 * Returns the accurate total count from the database when available,
 * falling back to the length of the locally-loaded page.
 */
export const selectDealsCount = (state: RootState) =>
  state.deals.totalCount ?? selectDeals(state).length;

export const selectDealsTotalValue = (state: RootState) =>
  selectDeals(state).reduce((total, deal) => total + deal.dealValue, 0);

/**
 * Selector: Deal status breakdown for dashboard widget
 * Returns counts for each deal stage matching the Kanban board
 */
export const selectDealStatusBreakdown = createSelector([selectDeals], (deals) => {
  const pitching = deals.filter((d) => d.stage === 'pitching').length;
  const negotiating = deals.filter((d) => d.stage === 'negotiating').length;
  const contractSigned = deals.filter((d) => d.stage === 'contract_signed').length;
  const contentInProgress = deals.filter((d) => d.stage === 'content_in_progress').length;
  const awaitingPayment = deals.filter((d) => d.stage === 'awaiting_payment').length;
  const completed = deals.filter((d) => d.stage === 'completed').length;
  const total = deals.length;

  return {
    pitching,
    negotiating,
    contractSigned,
    contentInProgress,
    awaitingPayment,
    completed,
    total,
  };
});

/**
 * Selector: Deal status breakdown filtered by date range
 */
/**
 * Selector: Deal status breakdown filtered by date range
 */
export const selectDealStatusBreakdownByDateRange = createSelector(
  [selectDeals, (_state: RootState, dateRange: DateRange) => dateRange],
  (deals, dateRange) => {
    const startDate = getDateRangeStart(dateRange);

    // Filter deals by date range
    const filteredDeals = startDate
      ? deals.filter((d) => new Date(d.createdAt) >= startDate)
      : deals;

    const pitching = filteredDeals.filter((d) => d.stage === 'pitching').length;
    const negotiating = filteredDeals.filter((d) => d.stage === 'negotiating').length;
    const contractSigned = filteredDeals.filter((d) => d.stage === 'contract_signed').length;
    const contentInProgress = filteredDeals.filter((d) => d.stage === 'content_in_progress').length;
    const awaitingPayment = filteredDeals.filter((d) => d.stage === 'awaiting_payment').length;
    const completed = filteredDeals.filter((d) => d.stage === 'completed').length;
    const total = filteredDeals.length;

    return {
      pitching,
      negotiating,
      contractSigned,
      contentInProgress,
      awaitingPayment,
      completed,
      total,
    };
  }
);

/**
 * Selector: Active deals count (not completed)
 */
export const selectActiveDealsCount = createSelector([selectActiveDeals], (activeDeals) => ({
  count: activeDeals.length,
}));

/**
 * Selector: Total active deals count as a plain number.
 * Returns the accurate server-side count when available, otherwise falls back to
 * the length of the locally-loaded active deals array.
 */
export const selectTotalActiveDealsCount = (state: RootState): number =>
  state.deals.totalActive ?? selectDeals(state).filter((d) => d.stage !== 'completed').length;

/**
 * Selector: Active deals count filtered by date range
 */
/**
 * Selector: Active deals count filtered by date range
 */
export const selectActiveDealsCountByDateRange = createSelector(
  [selectActiveDeals, (_state: RootState, dateRange: DateRange) => dateRange],
  (deals, dateRange) => {
    const startDate = getDateRangeStart(dateRange);

    const filteredDeals = startDate
      ? deals.filter((d) => new Date(d.createdAt) >= startDate)
      : deals;

    return { count: filteredDeals.length };
  }
);

// Memoized selector for deals pipeline (prevents unnecessary rerenders)
export const selectDealsPipeline = createSelector([selectDeals, selectBrands], (deals, brands) => {
  return DEAL_STAGE_ORDER.map((stage) => {
    const dealsInStage = deals
      .filter((deal) => deal.stage === stage)
      .map((deal) => {
        const brand = brands.find((b) => b.id === deal.brandId);
        return {
          ...deal,
          brandName: brand?.brandName,
        };
      });

    return {
      stage,
      deals: dealsInStage,
      count: dealsInStage.length,
      totalValue: dealsInStage.reduce((total, deal) => total + deal.dealValue, 0),
    };
  });
});

// Memoized selector for filtered deals pipeline
export const selectFilteredDealsPipeline = createSelector(
  [selectDeals, selectBrands, (state: RootState) => state.deals.filters],
  (deals, brands, filters) => {
    // Apply filters to deals
    const filteredDeals = deals.filter((deal) => {
      const brand = brands.find((b) => b.id === deal.brandId);
      const brandName = brand?.brandName || '';

      // Search filter (search in deal name or brand name)
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const matchesSearch =
          deal.dealName.toLowerCase().includes(searchLower) ||
          brandName.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }

      // Brand IDs filter
      if (filters.brandIds && filters.brandIds.length > 0) {
        if (!filters.brandIds.includes(deal.brandId)) return false;
      }

      return true;
    });

    // Group filtered deals by stage
    return DEAL_STAGE_ORDER.map((stage) => {
      const dealsInStage = filteredDeals
        .filter((deal) => deal.stage === stage)
        .map((deal) => {
          const brand = brands.find((b) => b.id === deal.brandId);
          return {
            ...deal,
            brandName: brand?.brandName,
          };
        });

      return {
        stage,
        deals: dealsInStage,
        count: dealsInStage.length,
        totalValue: dealsInStage.reduce((total, deal) => total + deal.dealValue, 0),
      };
    });
  }
);

/**
 * Helper: Get completion date from deal's stage history
 * Returns the LATEST completion date (last time deal was moved to completed)
 * Falls back to updatedAt if no completion entry found
 * Returns null if deal is not currently in completed stage
 */
const getCompletionDate = (deal: Deal): Date | null => {
  // Only count revenue for deals currently in completed stage
  if (deal.stage !== 'completed') return null;

  // Safety check: if no stageHistory, fall back to updatedAt
  if (!deal.stageHistory || !Array.isArray(deal.stageHistory) || deal.stageHistory.length === 0) {
    return new Date(deal.updatedAt);
  }

  // Find all completion entries and get the latest one
  const completionEntries = deal.stageHistory.filter((entry) => entry.stage === 'completed');

  if (completionEntries.length === 0) {
    // Fallback to updatedAt if no stage history entry found
    return new Date(deal.updatedAt);
  }

  // Get the latest completion entry (last in array, as entries are added chronologically)
  const latestEntry = completionEntries[completionEntries.length - 1];
  return latestEntry ? new Date(latestEntry.changedAt) : new Date(deal.updatedAt);
};

/**
 * Helper: Check if date falls within range
 */
const isDateInRange = (date: Date, start: Date, end: Date): boolean => {
  return date >= start && date <= end;
};

/**
 * Selector: All completed deals
 */
export const selectCompletedDeals = createSelector([selectDeals], (deals) =>
  deals.filter((deal) => deal.stage === 'completed')
);

/**
 * Selector: Total revenue from completed deals (all time)
 */
export const selectCompletedDealsTotalRevenue = createSelector(
  [selectCompletedDeals],
  (completedDeals) =>
    completedDeals.reduce((total, deal) => {
      const value =
        typeof deal.dealValue === 'number' && !isNaN(deal.dealValue) ? deal.dealValue : 0;
      return total + value;
    }, 0)
);

/**
 * Selector: Revenue statistics with week-over-week comparison
 */
export const selectRevenueStats = createSelector([selectCompletedDeals], (completedDeals) => {
  const now = new Date();
  const currentWeekStart = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const previousWeekStart = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);

  let currentWeekRevenue = 0;
  let previousWeekRevenue = 0;

  completedDeals.forEach((deal) => {
    const completionDate = getCompletionDate(deal);
    if (!completionDate) return;

    const value = typeof deal.dealValue === 'number' && !isNaN(deal.dealValue) ? deal.dealValue : 0;

    // Current week (last 7 days)
    if (isDateInRange(completionDate, currentWeekStart, now)) {
      currentWeekRevenue += value;
    }

    // Previous week (8-14 days ago)
    if (isDateInRange(completionDate, previousWeekStart, currentWeekStart)) {
      previousWeekRevenue += value;
    }
  });

  // Calculate percentage change
  let percentageChange = 0;
  if (previousWeekRevenue === 0) {
    percentageChange = currentWeekRevenue > 0 ? 100 : 0;
  } else {
    percentageChange = ((currentWeekRevenue - previousWeekRevenue) / previousWeekRevenue) * 100;
  }

  return {
    currentWeekRevenue,
    previousWeekRevenue,
    percentageChange,
    totalRevenue: completedDeals.reduce((total, deal) => {
      const value =
        typeof deal.dealValue === 'number' && !isNaN(deal.dealValue) ? deal.dealValue : 0;
      return total + value;
    }, 0),
  };
});

/**
 * Selector: Revenue statistics filtered by date range
 */
/**
 * Selector: Revenue statistics filtered by date range
 */
export const selectRevenueStatsByDateRange = createSelector(
  [selectCompletedDeals, (_state: RootState, dateRange: DateRange) => dateRange],
  (completedDeals, dateRange) => {
    const startDate = getDateRangeStart(dateRange);

    // Filter completed deals by date range (using completion date)
    const filteredDeals = startDate
      ? completedDeals.filter((deal) => {
          const completionDate = getCompletionDate(deal);
          return completionDate && completionDate >= startDate;
        })
      : completedDeals;

    // Calculate total revenue with safety checks for invalid deal values
    const totalRevenue = filteredDeals.reduce((total, deal) => {
      const value =
        typeof deal.dealValue === 'number' && !isNaN(deal.dealValue) ? deal.dealValue : 0;
      return total + value;
    }, 0);

    // Calculate period-over-period change based on the date range
    const now = new Date();
    let periodLength: number;

    switch (dateRange) {
      case 'day':
        periodLength = 24 * 60 * 60 * 1000;
        break;
      case 'week':
        periodLength = 7 * 24 * 60 * 60 * 1000;
        break;
      case 'ytd':
      case '1year':
        periodLength = 365 * 24 * 60 * 60 * 1000;
        break;
      case '3years':
        periodLength = 3 * 365 * 24 * 60 * 60 * 1000;
        break;
      default:
        periodLength = 7 * 24 * 60 * 60 * 1000;
    }

    // Calculate previous period revenue for comparison
    const previousPeriodStart = startDate
      ? new Date(startDate.getTime() - periodLength)
      : new Date(now.getTime() - periodLength);
    const previousPeriodEnd = startDate || now;

    const previousPeriodDeals = completedDeals.filter((deal) => {
      const completionDate = getCompletionDate(deal);
      return (
        completionDate &&
        completionDate >= previousPeriodStart &&
        completionDate < previousPeriodEnd
      );
    });
    const previousPeriodRevenue = previousPeriodDeals.reduce((total, deal) => {
      const value =
        typeof deal.dealValue === 'number' && !isNaN(deal.dealValue) ? deal.dealValue : 0;
      return total + value;
    }, 0);

    // Calculate percentage change
    let percentageChange = 0;
    if (previousPeriodRevenue === 0) {
      percentageChange = totalRevenue > 0 ? 100 : 0;
    } else {
      percentageChange = ((totalRevenue - previousPeriodRevenue) / previousPeriodRevenue) * 100;
    }

    return {
      totalRevenue,
      previousPeriodRevenue,
      percentageChange,
    };
  }
);

/**
 * Revenue time series data point
 */
export interface RevenueDataPoint {
  label: string;
  value: number;
}

/**
 * Selector: Revenue time series for chart by date range
 * Returns revenue aggregated into appropriate time buckets
 */
/**
 * Selector: Revenue time series for chart by date range
 * Returns revenue aggregated into appropriate time buckets
 */
export const selectRevenueTimeSeriesByDateRange = createSelector(
  [selectCompletedDeals, (_state: RootState, dateRange: DateRange) => dateRange],
  (completedDeals, dateRange) => {
    const startDate = getDateRangeStart(dateRange);
    const now = new Date();

    // Filter completed deals by date range
    const filteredDeals = startDate
      ? completedDeals.filter((deal) => {
          const completionDate = getCompletionDate(deal);
          return completionDate && completionDate >= startDate;
        })
      : completedDeals;

    // Determine bucket type based on date range
    type BucketType = 'single_day' | 'day' | 'month' | 'year';
    let bucketType: BucketType;
    let bucketCount: number;

    switch (dateRange) {
      case 'day':
        bucketType = 'single_day';
        bucketCount = 1;
        break;
      case 'week':
        bucketType = 'day';
        bucketCount = 7;
        break;
      case 'ytd':
      case '1year':
        bucketType = 'month';
        bucketCount = 12;
        break;
      case '3years':
        bucketType = 'year';
        bucketCount = 3;
        break;
      case 'max':
      default:
        bucketType = 'year';
        bucketCount = 5; // Show last 5 years for max
        break;
    }

    // Generate bucket labels and aggregate revenue
    const dataPoints: RevenueDataPoint[] = [];

    if (bucketType === 'single_day') {
      // Single day - just today's revenue
      const today = new Date(now);
      today.setHours(0, 0, 0, 0);
      const endOfDay = new Date(today);
      endOfDay.setDate(endOfDay.getDate() + 1);

      const dayRevenue = filteredDeals
        .filter((deal) => {
          const completionDate = getCompletionDate(deal);
          return completionDate && completionDate >= today && completionDate < endOfDay;
        })
        .reduce((sum, deal) => sum + deal.dealValue, 0);

      const label = today.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'short',
        day: 'numeric',
      });
      dataPoints.push({ label, value: dayRevenue });
    } else if (bucketType === 'day') {
      // Aggregate by day
      for (let i = bucketCount - 1; i >= 0; i--) {
        const date = new Date(now);
        date.setDate(date.getDate() - i);
        date.setHours(0, 0, 0, 0);
        const endOfDay = new Date(date);
        endOfDay.setDate(endOfDay.getDate() + 1);

        const dayRevenue = filteredDeals
          .filter((deal) => {
            const completionDate = getCompletionDate(deal);
            return completionDate && completionDate >= date && completionDate < endOfDay;
          })
          .reduce((sum, deal) => sum + deal.dealValue, 0);

        const label = date.toLocaleDateString('en-US', { weekday: 'short' });
        dataPoints.push({ label, value: dayRevenue });
      }
    } else if (bucketType === 'month') {
      // Aggregate by month
      for (let i = bucketCount - 1; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const endOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 1);

        const monthRevenue = filteredDeals
          .filter((deal) => {
            const completionDate = getCompletionDate(deal);
            return completionDate && completionDate >= date && completionDate < endOfMonth;
          })
          .reduce((sum, deal) => sum + deal.dealValue, 0);

        const label = date.toLocaleDateString('en-US', { month: 'short' });
        dataPoints.push({ label, value: monthRevenue });
      }
    } else {
      // Aggregate by year
      for (let i = bucketCount - 1; i >= 0; i--) {
        const year = now.getFullYear() - i;
        const startOfYear = new Date(year, 0, 1);
        const endOfYear = new Date(year + 1, 0, 1);

        const yearRevenue = filteredDeals
          .filter((deal) => {
            const completionDate = getCompletionDate(deal);
            return completionDate && completionDate >= startOfYear && completionDate < endOfYear;
          })
          .reduce((sum, deal) => sum + deal.dealValue, 0);

        dataPoints.push({ label: String(year), value: yearRevenue });
      }
    }

    return dataPoints;
  }
);

/**
 * Selector: Pipeline value (total value of all active/in-progress deals)
 */
export const selectPipelineValue = createSelector([selectActiveDeals], (activeDeals) =>
  activeDeals.reduce((total, deal) => {
    const value = typeof deal.dealValue === 'number' && !isNaN(deal.dealValue) ? deal.dealValue : 0;
    return total + value;
  }, 0)
);

/**
 * Selector: Deals awaiting payment
 */
export const selectDealsAwaitingPayment = createSelector([selectDeals], (deals) =>
  deals.filter((deal) => deal.stage === 'awaiting_payment')
);

/**
 * Selector: Count of deals awaiting payment
 */
export const selectDealsAwaitingPaymentCount = createSelector(
  [selectDealsAwaitingPayment],
  (deals) => deals.length
);

/**
 * Export actions and reducer
 */
export const {
  setSelectedDeal,
  clearSelectedDeal,
  setDealFilters,
  clearDealFilters,
  clearError,
  resetDealState,
} = dealSlice.actions;

export default dealSlice.reducer;
